import { useEffect, useState } from 'react';
import { previewEpisodeCountdown } from '../utils/previewEpisodeCountdown';

const motionQuery = '(prefers-reduced-motion: reduce)';

const usePreviewEpisodeCountdown = ({ audio, mediaURL, fallbackDuration, enabled }) => {
    const [sample, setSample] = useState(null);
    const [reducedMotion, setReducedMotion] = useState(() => Boolean(window.matchMedia?.(motionQuery).matches));

    useEffect(() => {
        const query = window.matchMedia?.(motionQuery);
        if (!query) return undefined;
        const changed = () => setReducedMotion(query.matches);
        query.addEventListener?.('change', changed);
        return () => query.removeEventListener?.('change', changed);
    }, []);

    useEffect(() => {
        if (!audio || !enabled) {
            setSample(null);
            return undefined;
        }
        let frame = null;
        let lastFrame = 0;
        let buffering = audio.readyState < HTMLMediaElement.HAVE_FUTURE_DATA;
        const ownsMedia = () => audio.src === mediaURL;
        const canAnimate = () => ownsMedia() && !audio.paused && !audio.ended
            && !audio.seeking && !buffering && !audio.error;
        const stop = () => {
            if (frame !== null) cancelAnimationFrame(frame);
            frame = null;
        };
        const update = (eventName) => {
            if (!ownsMedia() || audio.ended || audio.error) {
                setSample(null);
                return;
            }
            const duration = Number.isFinite(audio.duration) && audio.duration > 0
                ? audio.duration : Number(fallbackDuration);
            const value = previewEpisodeCountdown(duration, audio.currentTime, audio.playbackRate);
            const running = canAnimate();
            setSample(previous => {
                // A paused/buffering timeupdate does not run the fill. Explicit
                // seek, rate and metadata events still recalculate its position.
                if (eventName === 'timeupdate' && !running && previous?.mediaURL === mediaURL) {
                    return previous.running ? { ...previous, running: false } : previous;
                }
                if (!value) return null;
                const next = { ...value, mediaURL, running };
                return previous?.mediaURL === mediaURL && previous.progress === next.progress
                    && previous.secondsRemaining === next.secondsRemaining && previous.running === running
                    ? previous : next;
            });
        };
        const tick = timestamp => {
            frame = null;
            if (!canAnimate()) return;
            if (timestamp - lastFrame >= 32) {
                lastFrame = timestamp;
                update();
            }
            frame = requestAnimationFrame(tick);
        };
        const synchronize = event => {
            const name = event?.type;
            if (['waiting', 'stalled', 'loadstart', 'emptied'].includes(name)) buffering = true;
            if (['playing', 'canplay'].includes(name)) buffering = false;
            stop();
            if (['loadstart', 'emptied'].includes(name)) setSample(null);
            else update(name);
            if (!reducedMotion && canAnimate()) frame = requestAnimationFrame(tick);
        };
        const events = ['timeupdate', 'durationchange', 'loadedmetadata', 'ratechange', 'seeking',
            'seeked', 'play', 'playing', 'pause', 'waiting', 'stalled', 'canplay', 'emptied',
            'loadstart', 'ended', 'error'];
        synchronize();
        events.forEach(name => audio.addEventListener(name, synchronize));
        return () => {
            stop();
            events.forEach(name => audio.removeEventListener(name, synchronize));
        };
    }, [audio, enabled, fallbackDuration, mediaURL, reducedMotion]);

    return enabled && sample?.mediaURL === mediaURL && audio?.src === mediaURL
        ? sample : null;
};

export default usePreviewEpisodeCountdown;
